import React, { useCallback, useEffect, useMemo, useState } from "react";
import { globalAiApi } from "@/api/africaGlobalAiClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — AdminOne Global AI Investment Watchtower™ (Phase 19)
 *
 * Strictly additive page. Surfaces the Global AI Investment Watchtower™:
 *   - Regional pulse (6 macro regions)
 *   - Sub-domain heatmap (14 verticals)
 *   - Region drilldown (anchors, layered scores, signals)
 *   - LOGIX-2 Global AI Investment Brief™
 *   - Personal watchlist (Pro+)
 */

function Pill({ children, color = "#7ac860", on = true, onClick }) {
  return (
    <button
      type="button"
      className="mesh-pill"
      onClick={onClick}
      style={{
        borderColor: color,
        color: on ? color : "var(--clx-muted)",
        background: on ? `${color}1a` : "transparent",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {children}
    </button>
  );
}

function Bar({ value = 0, color = "#7ac860" }) {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div style={{ height: 8, width: "100%", background: "rgba(255,255,255,.06)", borderRadius: 999, overflow: "hidden" }}>
      <div style={{ width: `${v}%`, height: "100%", background: color, borderRadius: 999 }} />
    </div>
  );
}

function scoreColor(score) {
  if (score >= 90) return "#c9a544";
  if (score >= 80) return "#7ac860";
  if (score >= 70) return "#60b8e8";
  if (score >= 60) return "#f5c842";
  return "#e87040";
}

export default function AdminGlobalAIWatchtower() {
  const [pulse, setPulse] = useState(null);
  const [brief, setBrief] = useState(null);
  const [signals, setSignals] = useState([]);
  const [selected, setSelected] = useState("NA");
  const [detail, setDetail] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("pulse");
  const [newSymbol, setNewSymbol] = useState("");
  const [newNote, setNewNote] = useState("");

  const loadCore = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [{ data: p }, { data: b }, { data: s }, { data: w }] = await Promise.all([
        globalAiApi.pulse(false),
        globalAiApi.dailyBrief(),
        globalAiApi.signals(30),
        globalAiApi.watchlistGet(),
      ]);
      setPulse(p);
      setBrief(b);
      setSignals(s.signals || []);
      setWatchlist(w.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load Global AI watchtower");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { loadCore(); }, [loadCore]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await globalAiApi.region(selected);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) setDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const addWatch = useCallback(async () => {
    if (!newSymbol.trim()) return;
    setBusy(true);
    try {
      await globalAiApi.watchlistAdd({ symbol: newSymbol.trim().toUpperCase(), note: newNote.trim() || null });
      setNewSymbol(""); setNewNote("");
      const { data: w } = await globalAiApi.watchlistGet();
      setWatchlist(w.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to add");
    } finally { setBusy(false); }
  }, [newSymbol, newNote]);

  const removeWatch = useCallback(async (id) => {
    setBusy(true);
    try {
      await globalAiApi.watchlistDelete(id);
      const { data: w } = await globalAiApi.watchlistGet();
      setWatchlist(w.items || []);
    } finally { setBusy(false); }
  }, []);

  const tier = pulse?.tier || "professional";
  const subTop = useMemo(() => (pulse?.subdomains || []).slice(0, 14), [pulse]);
  const regions = useMemo(() => pulse?.regions || [], [pulse]);

  return (
    <div className="admin-page" data-testid="admin-global-ai-watchtower">
      {/* Phase 26C — Mandated Compliance Lock™ banner */}
      <ComplianceLock variant="banner" testid="globalai-watchtower-compliance" />
      <header className="admin-page__hd">
        <div>
          <h1 className="admin-page__title">Global AI Investment Watchtower™</h1>
          <p className="admin-page__sub">LOGIX-2™ · Global AI Oracle Score™ · 6 regions · 14 sub-domains · daily intelligence</p>
        </div>
        <div className="admin-page__badges" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span className="admin-badge is-on">LIVE</span>
          <span className="admin-badge is-on">COMPLIANCE GUARD</span>
          <span className="admin-badge">Tier: {String(tier).toUpperCase()}</span>
        </div>
      </header>

      {err && <div className="oracle-error" data-testid="admin-global-ai-error">{err}</div>}

      <div className="admin-tabs" role="tablist" style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "12px 0" }}>
        {[{ k: "pulse", label: "AI Pulse" }, { k: "brief", label: "Daily Brief" }, { k: "signals", label: "Signals" }, { k: "watchlist", label: "Watchlist" }].map((t) => (
          <button
            key={t.k}
            type="button"
            role="tab"
            aria-selected={tab === t.k}
            className={`oracle-btn ${tab === t.k ? "oracle-btn--gold" : "oracle-btn--ghost"}`}
            onClick={() => setTab(t.k)}
            data-testid={`admin-global-ai-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
        <button type="button" className="oracle-btn oracle-btn--ghost" onClick={loadCore} disabled={busy} data-testid="admin-global-ai-refresh">
          {busy ? "…" : "↻ Refresh"}
        </button>
      </div>

      {tab === "pulse" && (
        <>
          <section className="admin-section" data-testid="admin-global-ai-regions">
            <h3 className="admin-section__title">Regional Heatmap · Global AI Oracle Score™</h3>
            <div className="admin-metric-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
              {regions.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => { setSelected(r.code); setTab("signals"); }}
                  className="admin-metric"
                  data-testid={`admin-global-ai-region-${r.code}`}
                  style={{ cursor: "pointer", textAlign: "left" }}
                >
                  <div className="admin-metric__label">{r.name}</div>
                  <div className="admin-metric__value" style={{ color: scoreColor(r.global_ai_oracle_score) }}>{r.global_ai_oracle_score}</div>
                  <div style={{ fontSize: 11, color: "var(--clx-muted)" }}>{r.grade}</div>
                  <Bar value={r.global_ai_oracle_score} color={scoreColor(r.global_ai_oracle_score)} />
                </button>
              ))}
            </div>
          </section>

          <section className="admin-section" data-testid="admin-global-ai-subdomains">
            <h3 className="admin-section__title">Sub-domain Heatmap</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10 }}>
              {subTop.map((s) => (
                <div key={s.subdomain} className="admin-card" style={{ padding: 12, border: "1px solid var(--clx-border)", borderRadius: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                    <strong style={{ fontSize: 13 }}>{s.subdomain}</strong>
                    <span style={{ color: scoreColor(s.score), fontWeight: 700 }}>{s.score}</span>
                  </div>
                  <Bar value={s.score} color={scoreColor(s.score)} />
                  <div style={{ fontSize: 10, color: "var(--clx-muted)", marginTop: 4 }}>{s.grade}</div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {tab === "brief" && (
        <section className="admin-section" data-testid="admin-global-ai-brief">
          <h3 className="admin-section__title">{brief?.title || "LOGIX-2 Global AI Investment Brief™"}</h3>
          <p className="admin-foot-note">Generated: {brief?.generated_at}</p>
          {(brief?.sections || []).map((sec) => (
            <div key={sec.id} className="admin-card" style={{ marginTop: 12, padding: 14, border: "1px solid var(--clx-border)", borderRadius: 10 }} data-testid={`admin-global-ai-brief-section-${sec.id}`}>
              <h4 style={{ margin: 0, marginBottom: 6, color: "var(--clx-gold)", fontFamily: "'Cinzel',serif", letterSpacing: ".06em" }}>{sec.title}</h4>
              {sec.content && <p style={{ margin: 0, color: "var(--clx-text-bright,#fff7d6)" }}>{sec.content}</p>}
              {Array.isArray(sec.rows) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.rows.map((r, i) => (
                    <li key={i} style={{ fontSize: 13, lineHeight: 1.6 }}>
                      <strong>{r.name}</strong> · score <span style={{ color: scoreColor(r.score) }}>{r.score}</span> · {r.grade}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(sec.signals) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.signals.map((s) => (
                    <li key={s.id} style={{ fontSize: 13, lineHeight: 1.6, color: s.color || "inherit" }}>
                      <strong>{s.label}</strong> · {s.text}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(sec.adds) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.adds.map((a, i) => (
                    <li key={i} style={{ fontSize: 13 }}><code>{a.symbol}</code> · {a.name || a.region} · {a.reason}</li>
                  ))}
                </ul>
              )}
              {Array.isArray(sec.items) && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {sec.items.length === 0 ? <li style={{ color: "var(--clx-muted)" }}>None flagged for today.</li> : sec.items.map((it, i) => <li key={i} style={{ fontSize: 13 }}>{it}</li>)}
                </ul>
              )}
            </div>
          ))}
          {brief?.disclaimer && <p className="admin-foot-note" style={{ marginTop: 12 }}>{brief.disclaimer}</p>}
        </section>
      )}

      {tab === "signals" && (
        <>
          <section className="admin-section" data-testid="admin-global-ai-region-picker">
            <h3 className="admin-section__title">Region Detail</h3>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(pulse?.regions || []).map((r) => (
                <Pill key={r.code} color={scoreColor(r.global_ai_oracle_score)} on={selected === r.code} onClick={() => setSelected(r.code)}>
                  {r.name} · {r.global_ai_oracle_score}
                </Pill>
              ))}
            </div>
            {detail && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
                  <div className="admin-metric">
                    <div className="admin-metric__label">Global AI Oracle Score™</div>
                    <div className="admin-metric__value" style={{ color: scoreColor(detail.score.global_ai_oracle_score) }}>{detail.score.global_ai_oracle_score}</div>
                    <div style={{ fontSize: 11, color: "var(--clx-muted)" }}>{detail.score.grade}</div>
                  </div>
                  <div className="admin-metric">
                    <div className="admin-metric__label">Infrastructure Layer</div>
                    <div className="admin-metric__value" style={{ color: scoreColor(detail.layers.infrastructure) }}>{detail.layers.infrastructure}</div>
                    <Bar value={detail.layers.infrastructure} color={scoreColor(detail.layers.infrastructure)} />
                  </div>
                  <div className="admin-metric">
                    <div className="admin-metric__label">Model Layer</div>
                    <div className="admin-metric__value" style={{ color: scoreColor(detail.layers.model) }}>{detail.layers.model}</div>
                    <Bar value={detail.layers.model} color={scoreColor(detail.layers.model)} />
                  </div>
                  <div className="admin-metric">
                    <div className="admin-metric__label">Application Layer</div>
                    <div className="admin-metric__value" style={{ color: scoreColor(detail.layers.application) }}>{detail.layers.application}</div>
                    <Bar value={detail.layers.application} color={scoreColor(detail.layers.application)} />
                  </div>
                </div>

                <h4 style={{ marginTop: 14, color: "var(--clx-gold)" }}>Anchor Tickers · {detail.region.name}</h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(detail.anchors || []).map((a) => (
                    <Pill key={a.symbol} color={scoreColor(a.score)}>{a.symbol} · {a.score}</Pill>
                  ))}
                </div>

                <h4 style={{ marginTop: 14, color: "var(--clx-gold)" }}>Signals · {detail.region.name}</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10 }}>
                  {(detail.signals || []).map((s) => (
                    <div key={s.id} className="admin-card" style={{ padding: 12, border: `1px solid ${s.color}66`, borderRadius: 10, background: `${s.color}11` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <strong style={{ color: s.color }}>{s.icon} {s.label}</strong>
                        <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11 }}>{s.score}</span>
                      </div>
                      <p style={{ margin: "6px 0 0", fontSize: 13 }}>{s.text}</p>
                      <div style={{ fontSize: 10, color: "var(--clx-muted)", marginTop: 4 }}>{s.severity} · {s.tone}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="admin-section" data-testid="admin-global-ai-signals-feed">
            <h3 className="admin-section__title">Active Signals · Global AI</h3>
            <div style={{ overflowX: "auto" }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Region</th>
                    <th>Severity</th>
                    <th>Score</th>
                    <th>Text</th>
                  </tr>
                </thead>
                <tbody>
                  {(signals || []).map((s) => (
                    <tr key={s.id} data-testid={`admin-global-ai-signal-${s.type}`}>
                      <td><strong>{s.label}</strong></td>
                      <td><code>{s.scope}</code></td>
                      <td><Pill color={s.color}>{s.severity}</Pill></td>
                      <td style={{ color: scoreColor(s.score), fontWeight: 700 }}>{s.score}</td>
                      <td>{s.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {tab === "watchlist" && (
        <section className="admin-section" data-testid="admin-global-ai-watchlist">
          <h3 className="admin-section__title">My Global AI Watchlist</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <input className="oracle-input" placeholder="Symbol (e.g. NVDA, TSM, ASML)" value={newSymbol} onChange={(e) => setNewSymbol(e.target.value)} data-testid="admin-global-ai-watch-symbol" />
            <input className="oracle-input" placeholder="Note (optional)" value={newNote} onChange={(e) => setNewNote(e.target.value)} style={{ minWidth: 240 }} data-testid="admin-global-ai-watch-note" />
            <button type="button" className="oracle-btn oracle-btn--gold" onClick={addWatch} disabled={busy || !newSymbol.trim()} data-testid="admin-global-ai-watch-add">+ Add</button>
          </div>
          {watchlist.length === 0 ? (
            <div className="oracle-empty" style={{ padding: 14 }}>Watchlist empty. Add a public AI equity, ETF, or thesis ticker.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr><th>Symbol</th><th>Note</th><th>Added</th><th>Action</th></tr>
              </thead>
              <tbody>
                {watchlist.map((w) => (
                  <tr key={w.id} data-testid={`admin-global-ai-watch-row-${w.id}`}>
                    <td><code>{w.symbol}</code></td>
                    <td>{w.note || "—"}</td>
                    <td style={{ fontFamily: "'Space Mono',monospace", fontSize: 11 }}>{(w.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td><button type="button" className="oracle-btn oracle-btn--ghost oracle-btn--sm mesh-btn--danger" onClick={() => removeWatch(w.id)} data-testid={`admin-global-ai-watch-remove-${w.id}`}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <p className="admin-foot-note" style={{ marginTop: 16 }}>
        <em>{pulse?.disclaimer || "Research and education only. No guaranteed outcomes."}</em>
      </p>
    </div>
  );
}
